import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuPin, LuClock, LuTag, LuUser,
    LuLayoutGrid, LuList, LuNotebookPen, LuUsers, LuPencil,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import { ListCard, ListCards, ListRow, RowAction, RowActions } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import {
    AnotacaoService, tituloDaAnotacao, etiquetasDaAnotacao, ehDeOutroUsuario,
    type Anotacao,
} from '../services/AnotacaoService';
import './AnotacoesPage.css';

type Visao = 'mural' | 'lista';

const VISAO_KEY = 'lemeai_anotacoes_visao';

/** Data relativa curta: o cartão tem pouca linha e "há 2 dias" informa mais que 09/09. */
const quando = (iso: string): string => {
    const data = new Date(iso);
    const agora = new Date();
    const minutos = Math.floor((agora.getTime() - data.getTime()) / 60000);

    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;

    const mesmoDia = data.toDateString() === agora.toDateString();
    const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (mesmoDia) return `hoje ${hora}`;

    const ontem = new Date(agora);
    ontem.setDate(ontem.getDate() - 1);
    if (data.toDateString() === ontem.toDateString()) return `ontem ${hora}`;

    const dias = Math.floor((agora.getTime() - data.getTime()) / 86400000);
    if (dias < 7) return `há ${dias} dias`;

    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/* Nome da página · Criado por · Fonte · Última edição · Última visita · Ações.
   A coluna de ações é obrigatória no molde (decisão de 2026-09-10) e tem 78px. */
const COLUNAS = '2.4fr 1.2fr 1fr 1fr 1fr 78px';

const AnotacoesPage = () => {
    const navigate = useNavigate();
    const celular = useCelular();
    const [anotacoes, setAnotacoes] = useState<Anotacao[]>([]);
    const [carregando, setCarregando] = useState(true);
    /* Esta tela SAI para o editor (/anotacoes/:id) e volta: a busca se perdia a
       cada anotacao aberta. (A `visao` logo abaixo e preferencia de leitura e
       continua no localStorage, que sobrevive a fechar o navegador.) */
    const [recorte, gravarRecorte] = useRecorte('an:recorte', { busca: '' });
    const [busca, setBusca] = useState(recorte.busca);
    useEffect(() => { gravarRecorte({ busca }); }, [busca, gravarRecorte]);
    const [visao, setVisao] = useState<Visao>(
        () => (localStorage.getItem(VISAO_KEY) as Visao) || 'mural');

    useEffect(() => { localStorage.setItem(VISAO_KEY, visao); }, [visao]);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const r = await AnotacaoService.getAll();
            if (r.sucesso) setAnotacoes(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar as anotações.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return anotacoes;
        return anotacoes.filter(a =>
            tituloDaAnotacao(a).toLowerCase().includes(termo)
            || (a.resumo || '').toLowerCase().includes(termo)
            || (a.etiquetas || '').toLowerCase().includes(termo)
            || (a.nomeUsuarioCriador || '').toLowerCase().includes(termo));
    }, [anotacoes, busca]);

    const fixadas = filtradas.filter(a => a.fixada);
    const demais = filtradas.filter(a => !a.fixada);

    const abrir = (a: Anotacao) => navigate(`/anotacoes/${a.anotacaoId}`);

    // ─── CARTÃO (mural) ───────────────────────────────────────────
    const cartao = (a: Anotacao) => {
        const etiquetas = etiquetasDaAnotacao(a);
        const deOutro = ehDeOutroUsuario(a);

        return (
            <div
                key={a.anotacaoId}
                role="button"
                tabIndex={0}
                className="ano-card"
                aria-label={`Abrir anotação ${tituloDaAnotacao(a)}`}
                onClick={() => abrir(a)}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(a); }
                }}
            >
                <div className="ano-card-title">
                    {a.fixada && <LuPin size={14} className="ano-pin" />}
                    <span>{tituloDaAnotacao(a)}</span>
                </div>

                {a.resumo
                    ? <p className="ano-card-preview">{a.resumo}</p>
                    : <p className="ano-card-preview is-empty">Sem mais conteúdo.</p>}

                <div className="ano-card-foot">
                    {etiquetas.slice(0, 2).map(e => (
                        <span className="ano-chip" key={e}><LuTag size={11} />{e}</span>
                    ))}
                    {etiquetas.length > 2 && <span className="ano-chip">+{etiquetas.length - 2}</span>}
                    {a.compartilhada && (
                        <span className="ano-chip" title="Compartilhada com a equipe">
                            <LuUsers size={11} />equipe
                        </span>
                    )}
                    <span className="ano-sp" />
                    {deOutro
                        ? <span className="ano-meta"><LuUser size={11} />{a.nomeUsuarioCriador}</span>
                        : <span className="ano-meta">{quando(a.dataAtualizacao)}</span>}
                </div>
            </div>
        );
    };

    // ─── LINHA (tabela) ───────────────────────────────────────────
    const linha = (a: Anotacao) => (
        <ListRow
            key={a.anotacaoId}
            columns={COLUNAS}
            label={`Abrir a anotação ${tituloDaAnotacao(a)}`}
            onOpen={() => abrir(a)}
        >
            <span className="lk-cell lk-strong ano-nome">
                {a.fixada && <LuPin size={13} className="ano-pin" />}
                <span>{tituloDaAnotacao(a)}</span>
            </span>
            <span className="lk-cell lk-muted"><span>{a.nomeUsuarioCriador || '—'}</span></span>
            <span className="lk-cell">
                <span className={`lk-tag ${a.compartilhada ? 'ano-fonte-eq' : ''}`}>
                    {a.compartilhada ? 'Compartilhado' : 'Privado'}
                </span>
            </span>
            <span className="lk-cell lk-muted"><span>{quando(a.dataAtualizacao)}</span></span>
            <span className={`lk-cell ${a.ultimaVisita ? 'lk-muted' : 'lk-empty-value'}`}>
                <span>{a.ultimaVisita ? quando(a.ultimaVisita) : 'nunca aberta'}</span>
            </span>
            <RowActions>
                <RowAction
                    icon={<LuPencil size={14} />}
                    label={`Editar ${tituloDaAnotacao(a)}`}
                    onClick={() => abrir(a)}
                />
            </RowActions>
        </ListRow>
    );

    /* ─── CARTÃO (lista no celular) ───────────────────────────────
       A grade de seis colunas não cabe em 358px, então a linha vira cartão.
       Sem botão de ação: a única da linha é o lápis, que faz exatamente o que
       o toque no cartão já faz — repeti-lo seria dois alvos para a mesma porta,
       e aqui não existe exclusão para ocupar a borda direita. */
    const cartaoCelular = (a: Anotacao) => (
        <ListCard
            key={a.anotacaoId}
            titulo={
                <>
                    {/* O alfinete corre dentro do próprio título: `.lk-card-n` é
                        bloco com reticências, e um invólucro flex aqui tiraria o
                        corte do texto longo. */}
                    {a.fixada && <><LuPin size={13} className="ano-pin" />{' '}</>}
                    {tituloDaAnotacao(a)}
                </>
            }
            apoio={a.resumo || 'sem mais conteúdo'}
            estado={<span>editada {quando(a.dataAtualizacao)}</span>}
            label={`Abrir a anotação ${tituloDaAnotacao(a)}`}
            onOpen={() => abrir(a)}
        />
    );

    const vazio = !carregando && anotacoes.length === 0;
    const semResultado = !carregando && anotacoes.length > 0 && filtradas.length === 0;

    const acoesDoHeader = (
        <div className="ano-actions">
            <label className="ano-busca">
                <LuSearch size={14} />
                <input
                    type="text"
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar anotação"
                    aria-label="Buscar anotação"
                />
            </label>

            {/* Porta explícita: o alternador é um botão na tela, não um gesto
                escondido. Mural = cartões; lista = a tabela do molde. */}
            <div className="ano-visao" role="group" aria-label="Formato da lista">
                <button type="button" className={visao === 'mural' ? 'is-on' : ''}
                    onClick={() => setVisao('mural')} title="Mural" aria-pressed={visao === 'mural'}>
                    <LuLayoutGrid size={14} />
                </button>
                <button type="button" className={visao === 'lista' ? 'is-on' : ''}
                    onClick={() => setVisao('lista')} title="Lista" aria-pressed={visao === 'lista'}>
                    <LuList size={14} />
                </button>
            </div>

            {/* Sem anotação nenhuma, a porta é a do aviso, no meio da tela. */}
            {!vazio && (
                <button type="button" className="ano-novo" onClick={() => navigate('/anotacoes/nova')}>
                    <LuPlus size={15} />
                    <span>Nova anotação</span>
                </button>
            )}
        </div>
    );

    const blocoVazio = (
        <div className="ano-vazio">
            <LuNotebookPen size={22} />
            <p className="ano-vazio-titulo">Nenhuma anotação ainda</p>
            <p className="ano-vazio-texto">
                É aqui que moram ata de reunião, roteiro de ligação e playbook —
                o texto que não cabe numa linha dentro da oportunidade.
            </p>
            <button type="button" className="ano-novo" onClick={() => navigate('/anotacoes/nova')}>
                <LuPlus size={15} />
                <span>Nova anotação</span>
            </button>
        </div>
    );

    /* O aviso de "nada com esse termo" serve à tabela (no computador) e aos
       cartões (no celular) — por isso sai do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">Nada com esse termo</div>
            <p className="lk-blank-text">Ajuste a busca para ver as outras.</p>
        </div>
    );

    // ─── LISTA: a tabela do molde, igual às telas de cadastro ─────
    if (visao === 'lista') {
        return (
            <div className="page-shell lk-screen ano-screen">
                <PageHeader
                    title="Anotações"
                    subtitle="Suas anotações, atas e roteiros."
                    actions={acoesDoHeader}
                />
                <div className="page-body lk-fill">
                    {vazio ? blocoVazio : celular ? (
                        <>
                            <ListCards>
                                {carregando
                                    ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : [...fixadas, ...demais].map(cartaoCelular)}
                            </ListCards>
                            {semResultado && listaVazia}
                        </>
                    ) : (
                        <div className="lk-table">
                            <div className="lk-scroll">
                                <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                    <div className="lk-head">Nome da página</div>
                                    <div className="lk-head">Criado por</div>
                                    <div className="lk-head">Fonte</div>
                                    <div className="lk-head">Última edição</div>
                                    <div className="lk-head">Última visita</div>
                                    <div className="lk-head lk-head-acts">Ações</div>
                                </div>
                                <div className="lk-body">
                                    {carregando
                                        ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                        /* Fixadas primeiro, como na API — na tabela isso é
                                           ordenação, não seção: o alfinete na primeira coluna
                                           já diz qual é qual. */
                                        : [...fixadas, ...demais].map(linha)}
                                </div>
                            </div>

                            {semResultado && listaVazia}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ─── MURAL: a grade de cartões ────────────────────────────────
    return (
        <div className="page-shell ano-screen">
            <PageHeader
                title="Anotações"
                subtitle="Suas anotações, atas e roteiros."
                actions={acoesDoHeader}
            />

            <div className="page-body ano-body is-mural">
                {carregando && <p className="ano-estado">Carregando…</p>}
                {vazio && blocoVazio}
                {semResultado && (
                    <p className="ano-estado">Nenhuma anotação encontrada para “{busca}”.</p>
                )}

                {!carregando && fixadas.length > 0 && (
                    <>
                        <div className="ano-secao"><LuPin size={12} />Fixadas <span>{fixadas.length}</span></div>
                        <div className="ano-mural">{fixadas.map(cartao)}</div>
                    </>
                )}

                {!carregando && demais.length > 0 && (
                    <>
                        {fixadas.length > 0 && (
                            <div className="ano-secao ano-secao-2"><LuClock size={12} />Todas <span>{demais.length}</span></div>
                        )}
                        <div className="ano-mural">{demais.map(cartao)}</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AnotacoesPage;
